import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IoAddCircleOutline } from "react-icons/io5";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight, MdOutlineEdit, MdOutlinePostAdd } from 'react-icons/md';
import { CiViewList } from "react-icons/ci";
import { AiOutlineDelete } from "react-icons/ai";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getListPrice } from '../../store/ListPriceSlice';
import DeleteConfirmationModal from './../OsRequisition/RequisitionComponents/DeleteConfirmationModal';
import { useCurrent, useDelete } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { MwButton, NoDataFound } from '../ui';
import CardHeadeWithActions from './../ui/CardHeadeWithActions';

const OSviewAllListsPrices = ({ setIdSelected, setMode }) => {
  const { t, i18n } = useTranslation('global');
  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
  const dispatch = useDispatch();
  const { deleteItem, error: deleteError } = useDelete();

  const { ListData, loading, error } = useSelector((state) => state.ListPrice);
  const units = Array.isArray(ListData) ? ListData : [];

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (currentTenantId && currentCompanyId && currentBranchId) {
      dispatch(
        getListPrice(
          `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      );
    }
  }, [dispatch, currentTenantId, currentCompanyId, currentBranchId]);

  const refreshList = () => {
    dispatch(
      getListPrice(
        `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      )
    );
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = itemsPerPage === 10 ? units.slice(indexOfFirstItem, indexOfLastItem) : units;
  const totalPages = Math.ceil(units.length / itemsPerPage);

  const handleDelete = (id) => {
    setSelectedUnitId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const selectedUnit = units.find((unit) => unit.id === selectedUnitId);

    if (selectedUnit?.items?.length > 0) {
      toast.error(t("price.deleteError"));
      setDeleteModalOpen(false);
      return;
    }

    await deleteItem(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/list-price/${selectedUnitId}`);

    if (deleteError) {
      toast.error(t("price.deleteErrorGeneric"));
      return;
    }

    toast.success(t("price.deleteSuccess"));
    setDeleteModalOpen(false);
    setSelectedUnitId(null);
    refreshList();
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#251658]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">
          {t("price.loadError")} {error?.message || t("price.unknownError")}
        </p>
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
        <NoDataFound msg={t("price.noList")} />
        <div className="mt-4">
          <MwButton inGroup={true} type="saveBtn" onClick={() => setMode('AddList')}>
            <IoAddCircleOutline />
            <span>{t("price.addList")}</span>
          </MwButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-[600px] rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs">
      <ToastContainer />

      <CardHeadeWithActions title={t("price.listTitle")}>
        <MwButton inGroup={true} type="saveBtn" onClick={() => setMode('AddList')}>
          <IoAddCircleOutline />
          <span>{t("price.addList")}</span>
        </MwButton>
      </CardHeadeWithActions>

      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-[#EBF9D9] px-4 py-4 text-sm font-semibold text-[#251658]">
          <div className="col-span-1">#</div>
          <div className="col-span-4">{t("price.listName")}</div>
          <div className="col-span-2 text-center">{t("price.productsCount")}</div>
          <div className="col-span-2 text-center">{t("requisition.creation_date")}</div>
          <div className="col-span-1 text-center">{t("price.status")}</div>
          <div className="col-span-2 text-center">{t("table.actions")}</div>
        </div>

        <div className="max-h-[calc(100vh-320px)] overflow-y-auto custom-scrollbar">
          {currentItems.map((unit, index) => (
            <div
              key={unit.id}
              className="grid grid-cols-12 gap-3 border-b border-slate-100 px-4 py-3 text-sm text-slate-600 transition-colors hover:bg-[#251658]/5"
            >
              <div className="col-span-1 mt-2">{indexOfFirstItem + index + 1}</div>
              <div className="col-span-4 font-medium mt-2 text-slate-700">{unit?.name || t("price.unknownError")}</div>
              <div className="col-span-2 text-center mt-2">{unit?.items?.length || 0}</div>
              <div className="col-span-2 text-center mt-2">{new Date(unit?.created_at).toLocaleDateString("en-EG")}</div>
              <div className="col-span-1 flex mt-2 justify-center">
                <span className={`rounded-full mt-[-2px] py-1 px-2 h-fit text-xs ${unit.is_active ? 'bg-[#eef6e4] text-[#699635]' : 'bg-slate-200 text-slate-600'}`}>
                  {unit.is_active ? t("price.active") : t("price.inactive")}
                </span>
              </div>
              <div className="col-span-2 flex items-center  ] justify-center gap-2 text-[#251658]">
                <button type="button" onClick={() => { setIdSelected(unit.id); setMode('ViewList'); }} title={t("price.view")} className="rounded-full p-2 hover:bg-[#251658]/10">
                  <CiViewList size={18} />
                </button>
                <button type="button" onClick={() => { setIdSelected(unit.id); setMode('DetailsLists'); }} title={t("listPrice.add_items")} className="rounded-full p-2 hover:bg-[#251658]/10">
                  <MdOutlinePostAdd size={18} />
                </button>
                <button type="button" onClick={() => { setIdSelected(unit.id); setMode('EditList'); }} title={t("price.edit")} className="rounded-full p-2 hover:bg-[#251658]/10">
                  <MdOutlineEdit size={18} />
                </button>
                <button type="button" onClick={() => handleDelete(unit.id)} title={t("price.delete")} className="rounded-full p-2 text-red-500 hover:bg-red-50">
                  <AiOutlineDelete size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center gap-2 pb-1">
          <button
            onClick={() => {
              setCurrentPage(1);
              setItemsPerPage(units.length || itemsPerPage);
            }}
            className={`rounded px-3 py-1 ${
              itemsPerPage !== 10 ? 'bg-[#251658] text-white' : 'bg-slate-200 text-gray-600 hover:bg-slate-300'
            }`}
          >
            {t("common.all")}
          </button>

          {totalPages > 5 && currentPage > 3 && (
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className="rounded bg-slate-200 px-3 py-1 text-gray-600 hover:bg-slate-300"
            >
              {i18n.language === 'ar' ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
            </button>
          )}

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((number) => {
              if (totalPages <= 5) return true;
              if (number === 1 || number === totalPages) return true;
              return Math.abs(currentPage - number) < 3;
            })
            .map((number) => (
              <button
                key={number}
                onClick={() => {
                  handlePageChange(number);
                  setItemsPerPage(10);
                }}
                className={`rounded px-3 py-1 ${
                  currentPage === number && itemsPerPage === 10
                    ? 'bg-[#251658] text-white'
                    : 'bg-slate-200 text-gray-600 hover:bg-slate-300'
                }`}
              >
                {number}
              </button>
            ))}

          {totalPages > 5 && currentPage < totalPages - 2 && (
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className="rounded bg-slate-200 px-3 py-1 text-gray-600 hover:bg-slate-300"
            >
              {i18n.language === 'ar' ? <MdKeyboardArrowLeft size={20} /> : <MdKeyboardArrowRight size={20} />}
            </button>
          )}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        message={t('actions.confirmDelete')}
      />
    </div>
  );
};

export default OSviewAllListsPrices;
